import { animate, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, OnInit, output, ViewEncapsulation } from '@angular/core';
import { ToastData } from './toast.service';

@Component({
  selector: 'app-toast',
  templateUrl: './toast.component.html',
  styleUrls: ['toast.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('toastAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-16px) scale(0.95)' }),
        animate('250ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ opacity: 1, transform: 'translateY(0) scale(1)' }))
      ]),
      transition(':leave', [
        animate('200ms cubic-bezier(0.4, 0, 1, 1)',
          style({ opacity: 0, transform: 'translateY(-8px) scale(0.95)' }))
      ])
    ])
  ]
})
export class ToastComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  readonly data = input.required<ToastData>();
  readonly index = input.required<number>();
  readonly closed = output<void>();

  readonly toastClass = computed(() => `toast-item toast-${this.data().type}`);

  readonly iconPath = computed(() => {
    switch (this.data().type) {
      case 'success':
        return 'M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z';
      case 'error':
        return 'M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z';
      case 'warning':
        return 'M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z';
      case 'info':
        return 'M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z';
      case 'debug':
        return 'M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655-4.653a2.548 2.548 0 010-3.586l.823-.823a2.548 2.548 0 013.586 0l4.655 4.653M3.75 12l8.003-8.003a1.125 1.125 0 011.591 0l2.659 2.659a1.125 1.125 0 010 1.591L7.5 16.5';
      default:
        return 'M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z';
    }
  });

  readonly progressDuration = computed(() => {
    return this.data().duration ?? this.getDefaultDuration();
  });

  ngOnInit(): void {
    const duration = this.progressDuration();
    if (duration > 0) {
      const timeoutId = setTimeout(() => this.close(), duration);
      this.destroyRef.onDestroy(() => clearTimeout(timeoutId));
    }
  }

  close(): void {
    this.closed.emit();
  }

  private getDefaultDuration(): number {
    switch (this.data().type) {
      case 'error':
        return 5000;
      case 'warning':
        return 4000;
      case 'debug':
        return 8000;
      default:
        return 3000;
    }
  }
}
